import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { z } from "zod";
import { MailCheck, RefreshCw, ArrowLeft, Lock } from "lucide-react";
import syntraSymbol from "@/assets/syntra-symbol.svg";
import { useAuth } from "@/hooks/use-auth";
import {
  authModeForPath,
  pathForAuthMode,
  SIGN_IN_PATH,
  type AuthMode,
} from "@/lib/auth-routes";
import { strongPasswordSchema } from "@/lib/password";
import { PasswordField } from "@/components/auth/PasswordField";
import {
  PasswordMatchHint,
  PasswordRequirements,
} from "@/components/auth/PasswordRequirements";

const RESEND_COOLDOWN = 60;

const forgotEmailSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

const authSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  // Sign-in only checks that something was typed — the rules below apply when a
  // password is being set, and rejecting an older password here would just
  // lock people out of the account they need to sign in to fix it.
  password: z.string().min(1, "Please enter your password"),
});

const signUpSchema = authSchema.extend({
  password: strongPasswordSchema,
  fullName: z.string().min(1, "Please enter your name").max(200),
});

/**
 * Signup hit an address that already has an account. The second line covers the
 * signed-up-but-never-confirmed case, which Supabase reports identically to a
 * fully registered one.
 */
function toastAccountExists() {
  toast.error("This email already has an account", {
    description:
      "Switch to Sign In to continue. If you never confirmed it, check your inbox for the confirmation link.",
    position: "top-center",
  });
}

function EmailVerificationCard({
  email,
  onBack,
}: {
  email: string;
  onBack: () => void;
}) {
  const [countdown, setCountdown] = useState(RESEND_COOLDOWN);
  const [isResending, setIsResending] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startCountdown = () => {
    setCountdown(RESEND_COOLDOWN);
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current!);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    startCountdown();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const handleResend = async () => {
    try {
      setIsResending(true);
      const { error } = await supabase.auth.resend({ type: "signup", email });
      if (error) {
        toast.error(error.message);
        return;
      }
      toast.success("Verification email resent!");
      startCountdown();
    } catch {
      toast.error("Failed to resend email. Please try again.");
    } finally {
      setIsResending(false);
    }
  };

  return (
    <Card className="shadow-elevated border-border">
      <CardHeader className="text-center">
        <div className="flex justify-center mb-3">
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
            <MailCheck className="w-7 h-7 text-primary" />
          </div>
        </div>
        <CardTitle className="text-2xl">Check your inbox</CardTitle>
        <CardDescription>
          We sent a confirmation link to{" "}
          <span className="font-medium text-foreground">{email}</span>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground text-center">
          Click the link in the email to activate your account. The link expires
          in 24 hours.
        </p>

        <Button
          className="w-full gradient-primary text-white"
          onClick={handleResend}
          disabled={countdown > 0 || isResending}
        >
          {isResending ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Resending...
            </>
          ) : countdown > 0 ? (
            `Resend email in ${countdown}s`
          ) : (
            <>
              <RefreshCw className="w-4 h-4 mr-2" />
              Resend email
            </>
          )}
        </Button>

        <Button variant="ghost" className="w-full" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Sign In
        </Button>
      </CardContent>
    </Card>
  );
}

const recoverySchema = z
  .object({
    password: strongPasswordSchema,
    confirm: z.string().min(1, "Please confirm your password"),
  })
  .refine((data) => data.password === data.confirm, {
    message: "Passwords do not match",
    path: ["confirm"],
  });

function ForgotPasswordCard({
  initialEmail,
  onBack,
}: {
  initialEmail: string;
  onBack: () => void;
}) {
  const [email, setEmail] = useState(initialEmail);
  const [isLoading, setIsLoading] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);

  const handleSubmit = async () => {
    const parsed = forgotEmailSchema.safeParse({ email: email.trim() });
    if (!parsed.success) {
      toast.error(parsed.error.errors[0].message);
      return;
    }

    setIsLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(
      parsed.data.email,
      { redirectTo: `${window.location.origin}${SIGN_IN_PATH}` },
    );
    setIsLoading(false);

    if (error) {
      toast.error(error.message);
      return;
    }

    setSentTo(parsed.data.email);
    toast.success("Password reset email sent");
  };

  if (sentTo) {
    return (
      <Card className="shadow-elevated border-border">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-3">
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
              <MailCheck className="w-7 h-7 text-primary" />
            </div>
          </div>
          <CardTitle className="text-2xl">Check your inbox</CardTitle>
          <CardDescription>
            If an account exists for{" "}
            <span className="font-medium text-foreground">{sentTo}</span>, we
            sent a link to reset your password.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground text-center">
            The link expires in a short time. After you open it, you can choose a
            new password.
          </p>
          <Button variant="ghost" className="w-full" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Sign In
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-elevated border-border">
      <CardHeader className="text-center">
        <div className="flex justify-center mb-3">
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
            <Lock className="w-7 h-7 text-primary" />
          </div>
        </div>
        <CardTitle className="text-2xl">Forgot password?</CardTitle>
        <CardDescription>
          Enter your email and we&apos;ll send you a reset link.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="forgot-email">Email</Label>
          <Input
            id="forgot-email"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && void handleSubmit()}
          />
        </div>
        <Button
          className="w-full gradient-primary text-white"
          onClick={() => void handleSubmit()}
          disabled={isLoading}
        >
          {isLoading ? "Sending…" : "Send reset link"}
        </Button>
        <Button variant="ghost" className="w-full" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Sign In
        </Button>
      </CardContent>
    </Card>
  );
}

function PasswordRecoveryForm({
  email,
  onSuccess,
}: {
  email: string;
  onSuccess: () => void;
}) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async () => {
    const parsed = recoverySchema.safeParse({ password, confirm });
    if (!parsed.success) {
      const first = parsed.error.errors[0];
      toast.error(first.message);
      return;
    }

    setIsLoading(true);
    const { error } = await supabase.auth.updateUser({
      password: parsed.data.password,
    });
    setIsLoading(false);

    if (error) {
      toast.error(error.message);
      return;
    }

    toast.success("Password updated. You are signed in.");
    onSuccess();
  };

  return (
    <Card className="shadow-elevated border-border">
      <CardHeader className="text-center">
        <div className="flex justify-center mb-3">
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
            <Lock className="w-7 h-7 text-primary" />
          </div>
        </div>
        <CardTitle className="text-2xl">Set new password</CardTitle>
        <CardDescription>
          Choose a new password for{" "}
          <span className="font-medium text-foreground">{email}</span>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="recovery-password">New password</Label>
          <PasswordField
            id="recovery-password"
            autoComplete="new-password"
            placeholder="Enter a strong password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && void handleSubmit()}
          />
        </div>

        <PasswordRequirements value={password} />

        <div className="space-y-2">
          <Label htmlFor="recovery-confirm">Confirm password</Label>
          <PasswordField
            id="recovery-confirm"
            autoComplete="new-password"
            placeholder="Re-enter your password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && void handleSubmit()}
            invalid={!!confirm && confirm !== password}
          />
          <PasswordMatchHint password={password} confirm={confirm} />
        </div>

        <Button
          className="w-full gradient-primary text-white"
          onClick={() => void handleSubmit()}
          disabled={isLoading}
        >
          {isLoading ? "Updating…" : "Update password"}
        </Button>
      </CardContent>
    </Card>
  );
}

export default function Auth() {
  const {
    passwordRecoveryActive,
    clearPasswordRecovery,
    setSignupInProgress,
    user: sessionUser,
  } = useAuth();
  const location = useLocation();
  const activeMode = authModeForPath(location.pathname);
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPasswordRules, setShowPasswordRules] = useState(false);
  const [fullName, setFullName] = useState("");
  const [verificationEmail, setVerificationEmail] = useState("");
  const [showVerification, setShowVerification] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const navigate = useNavigate();

  const from =
    (location.state as { from?: { pathname: string } })?.from?.pathname ||
    "/dashboard";

  // The tabs are a view of the URL, not local state: switching one navigates so
  // the address bar always matches the visible form and stays shareable.
  // `replace` keeps the toggle out of history (Back returns to where they came
  // from), and the state carries the protected page they were headed to.
  const handleModeChange = (value: string) => {
    const nextMode: AuthMode = value === "signup" ? "signup" : "signin";
    if (nextMode === activeMode) return;
    navigate(pathForAuthMode(nextMode), {
      replace: true,
      state: location.state,
    });
  };

  const handleSubmit = async (mode: "signin" | "signup") => {
    try {
      setIsLoading(true);

      if (mode === "signup") {
        const signupParsed = signUpSchema.safeParse({
          email,
          password,
          fullName: fullName.trim(),
        });
        if (!signupParsed.success) {
          toast.error(signupParsed.error.errors[0].message);
          return;
        }

        const {
          email: signupEmail,
          password: signupPassword,
          fullName: name,
        } = signupParsed.data;
        const redirectUrl = `${window.location.origin}/`;
        // Auto-confirm signups get a session back from `signUp`, but the
        // profile (and the workspace its trigger creates) only exists after the
        // RPC below. Keep `PublicRoute` from redirecting on the session alone —
        // it would mount the dashboard against a not-yet-provisioned account.
        setSignupInProgress(true);
        const { data, error } = await supabase.auth.signUp({
          email: signupEmail,
          password: signupPassword,
          options: {
            emailRedirectTo: redirectUrl,
          },
        });

        if (error) {
          if (error.message.includes("already registered")) {
            toastAccountExists();
          } else {
            toast.error(error.message);
          }
          return;
        }

        const uid = data.user?.id;
        if (!uid) {
          toast.error("Could not create your account. Please try again.");
          return;
        }

        // With "Confirm email" on, Supabase hides whether an address is already
        // registered: instead of an error it returns a synthetic user — random
        // id, empty `identities` — and sends no mail. That id isn't in
        // auth.users, so `create_profile_after_signup` would reject it with
        // "invalid signup" (P0001). There is nothing to provision here; the
        // account already exists, so say so and send them to Sign In.
        if (data.user?.identities?.length === 0) {
          toastAccountExists();
          return;
        }

        const { error: profileError } = await supabase.rpc(
          "create_profile_after_signup",
          {
            p_user_id: uid,
            p_email: signupEmail,
            p_full_name: name,
          },
        );

        if (profileError) {
          toast.error(profileError.message);
          return;
        }

        if (!data.session) {
          setVerificationEmail(signupEmail);
          setShowVerification(true);
          return;
        }

        toast.success("Account created! Welcome to Syntra.");
        navigate(from, { replace: true });
        return;
      }

      const signinParsed = authSchema.safeParse({ email, password });
      if (!signinParsed.success) {
        toast.error(signinParsed.error.errors[0].message);
        return;
      }

      const { error } = await supabase.auth.signInWithPassword({
        email: signinParsed.data.email,
        password: signinParsed.data.password,
      });

      if (error) {
        if (error.message.includes("Invalid login credentials")) {
          toast.error("Invalid email or password. Please try again.");
        } else if (error.message.includes("Email not confirmed")) {
          toast.error(
            "Please confirm your email before signing in. Check your inbox.",
          );
        } else {
          toast.error(error.message);
        }
        return;
      }

      toast.success("Welcome back!");
      navigate(from, { replace: true });
    } catch {
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      setIsLoading(false);
      setSignupInProgress(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md">
        {/* Pinned while the form scrolls on short viewports. The negative
            margins pull it into the page padding so the resting position is
            unchanged, and the background keeps the card from showing through
            once it sticks. */}
        <div className="sticky top-0 z-10 -mx-4 -mt-4 mb-8 flex items-center justify-center gap-3 bg-background px-4 pt-4 pb-4">
          <img src={syntraSymbol} alt="Syntra" className="h-12 w-auto" />
          <span className="font-bold text-2xl gradient-syntra-blue-text">
            SYNTRA
          </span>
        </div>

        {showVerification ? (
          <EmailVerificationCard
            email={verificationEmail}
            onBack={() => {
              setShowVerification(false);
              setPassword("");
              setFullName("");
            }}
          />
        ) : passwordRecoveryActive ? (
          <PasswordRecoveryForm
            email={sessionUser?.email || "your account"}
            onSuccess={() => {
              clearPasswordRecovery();
              navigate(from, { replace: true });
            }}
          />
        ) : showForgotPassword ? (
          <ForgotPasswordCard
            initialEmail={email}
            onBack={() => setShowForgotPassword(false)}
          />
        ) : (
          <Card className="shadow-elevated border-border">
            <CardHeader className="text-center">
              {/* A shared /signup link should read as a signup page, not a
                  generic auth page — the heading follows the URL. */}
              <CardTitle className="text-2xl">
                {activeMode === "signup" ? "Create your account" : "Welcome back"}
              </CardTitle>
              <CardDescription>
                {activeMode === "signup"
                  ? "Start managing your portfolio in minutes"
                  : "Sign in to your Syntra account"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs value={activeMode} onValueChange={handleModeChange}>
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="signin">Sign In</TabsTrigger>
                  <TabsTrigger value="signup">Sign Up</TabsTrigger>
                </TabsList>

                <TabsContent value="signin" className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signin-email">Email</Label>
                    <Input
                      id="signin-email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onKeyDown={(e) =>
                        e.key === "Enter" && handleSubmit("signin")
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <Label htmlFor="signin-password">Password</Label>
                      <button
                        type="button"
                        className="text-xs text-secondary hover:underline"
                        onClick={() => setShowForgotPassword(true)}
                      >
                        Forgot password?
                      </button>
                    </div>
                    <PasswordField
                      id="signin-password"
                      autoComplete="current-password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyDown={(e) =>
                        e.key === "Enter" && handleSubmit("signin")
                      }
                    />
                  </div>
                  <Button
                    className="w-full gradient-primary text-white"
                    onClick={() => handleSubmit("signin")}
                    disabled={isLoading}
                  >
                    {isLoading ? "Signing in..." : "Sign In"}
                  </Button>
                </TabsContent>

                <TabsContent value="signup" className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signup-full-name">Full name</Label>
                    <Input
                      id="signup-full-name"
                      type="text"
                      autoComplete="name"
                      placeholder="Jane Doe"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      onKeyDown={(e) =>
                        e.key === "Enter" && handleSubmit("signup")
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-email">Email</Label>
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onKeyDown={(e) =>
                        e.key === "Enter" && handleSubmit("signup")
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-password">Password</Label>
                    <PasswordField
                      id="signup-password"
                      autoComplete="new-password"
                      placeholder="Enter a strong password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onFocus={() => setShowPasswordRules(true)}
                      onKeyDown={(e) =>
                        e.key === "Enter" && handleSubmit("signup")
                      }
                    />
                  </div>
                  {/* Kept out of the way until the field is in play, so the
                      sign-up form isn't a wall of rules on first sight. */}
                  {(showPasswordRules || password) && (
                    <PasswordRequirements
                      value={password}
                      className="animate-in fade-in-0 slide-in-from-top-1"
                    />
                  )}
                  <Button
                    className="w-full gradient-primary text-white"
                    onClick={() => handleSubmit("signup")}
                    disabled={isLoading}
                  >
                    {isLoading ? "Creating account..." : "Create Account"}
                  </Button>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        )}

        <p className="text-center text-sm text-muted-foreground mt-6">
          By continuing, you agree to our{" "}
          <a href="#" className="text-secondary hover:underline">
            Terms of Service
          </a>{" "}
          and{" "}
          <a href="#" className="text-secondary hover:underline">
            Privacy Policy
          </a>
          .
        </p>
      </div>
    </div>
  );
}
